import {
  Avatar,
  Button,
  Divider,
  Flex,
  message,
  Space,
  Table,
  TableColumnType,
  Tag,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './index.css';
import { LeftOutlined } from '@ant-design/icons';
import { CSSProperties, useMemo, useState } from 'react';
import {
  Artwork,
  ArtworkListResponse,
  ArtworkListSearchParams,
} from '@/types/contentReview';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import { useQuery } from '@tanstack/react-query';
import { getArtworkList } from '@/api/contentReview';
import { Gender, UserDetail, UserStatus } from '@/types/userMng';
import {
  batchForbiddenUser,
  batchUnforbiddenUser,
  batchUserDeAlert,
  batchUserWarn,
  getUserDetail,
} from '@/api/userMng';
import BanConfirmDialog from './banConfirmDialog';
import HistoryDialog from './historyDialog';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import DeAlertConfirmDialog from './deAlertConfirmDialog';
import AlertConfirmDialog from './alertConfirmDialog';

const DetailPage = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const [messageApi, contextHolder] = message.useMessage();

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [isBanConfirmDialogOpen, setIsBanConfirmDialogOpen] = useState(false);
  const [banConfirmDialogTitle, setBanConfirmDialogTitle] = useState('');
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [isForbidden, setIsForbidden] = useState(true);
  const [isAlertConfirmDialogOpen, setIsAlertConfirmDialogOpen] =
    useState(false);
  const [isDeAlertConfirmDialogOpen, setIsDeAlertConfirmDialogOpen] =
    useState(false);

  const labelStyle: CSSProperties = {
    width: '150px',
    flexShrink: 0,
  };

  const { data: userDetail, refetch: refetchUserDetail } = useQuery<
    UserDetail | undefined
  >({
    queryKey: ['userDetail', userId],
    queryFn: async () => {
      const resp = await getUserDetail(userId || '');
      if (resp.status !== 200) {
        return;
      }
      return resp.data;
    },
    enabled: !!userId,
  });

  const handleContentReview = (artworkId: string) => {
    navigate(`/content-review/detail?content_review_ids=${artworkId}`);
  };

  const columns: TableColumnType<Artwork>[] = [
    {
      title: '作品ID',
      dataIndex: 'artworkId',
      key: 'artworkId',
    },
    {
      title: '作品名',
      dataIndex: 'title',
      key: 'title',
      width: 150,
    },
    {
      title: '修改时间',
      dataIndex: 'reviewTime',
      key: 'reviewTime',
      render: (_, record) => {
        return <span>{dayjs(record.reviewTime).format(FORMAT_TIME)}</span>;
      },
    },
    {
      title: '审核状态',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
    },
    {
      title: '审核人',
      dataIndex: 'reviewUser',
      key: 'reviewUser',
    },
    {
      title: '操作',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleContentReview(record.artworkId)}
        >
          去审核
        </Button>
      ),
    },
  ];

  const { data: artworkList, isLoading } = useQuery<ArtworkListResponse>({
    queryKey: ['artworkList', page],
    queryFn: async () => {
      const params: ArtworkListSearchParams = {
        currentPage: page,
        pageSize,
        userId: userId || '',
      };
      const resp = await getArtworkList(params);
      return resp.data;
    },
    enabled: !!userId,
  });

  const userAge = useMemo(() => {
    if (!userDetail?.birthday) {
      return '未知';
    }
    return dayjs().diff(userDetail.birthday, 'year');
  }, [userDetail?.birthday]);

  const alertAction = () => {
    if (userDetail?.userStatus === UserStatus.ALERT) {
      return (
        <Button type="primary" onClick={handleDeAlert}>
          解除警告
        </Button>
      );
    }
    return (
      <Button type="primary" danger onClick={handleAlert}>
        警告
      </Button>
    );
  };

  const userStatusAction = () => {
    if (userDetail?.userStatus === UserStatus.BANNED) {
      return (
        <Button type="primary" onClick={handleUnban}>
          解封
        </Button>
      );
    }
    return (
      <Button type="primary" danger onClick={handleBan}>
        封禁
      </Button>
    );
  };

  const handleAlert = () => {
    setIsAlertConfirmDialogOpen(true);
  };

  const handleAlertConfirm = async (values: {
    alertDuration: number;
    alertReason: string;
  }) => {
    const resp = await batchUserWarn({
      userIds: userId || '',
      warnDesc: values.alertReason,
      warnTime: values.alertDuration,
    });
    if (resp.status !== 200) {
      messageApi.error('警告失败: ' + resp.message);
      return;
    }
    messageApi.success('警告成功');
    setIsAlertConfirmDialogOpen(false);
    void refetchUserDetail();
  };

  const handleDeAlert = () => {
    setIsDeAlertConfirmDialogOpen(true);
  };

  const handleDeAlertConfirm = async (values: { deAlertReason: string }) => {
    const resp = await batchUserDeAlert({
      userIds: userId || '',
      warnDesc: values.deAlertReason,
    });
    if (resp.status !== 200) {
      messageApi.error('解除警告失败: ' + resp.message);
      return;
    }
    messageApi.success('解除警告成功');
    setIsDeAlertConfirmDialogOpen(false);
    void refetchUserDetail();
  };

  const handleUnban = () => {
    setIsForbidden(false);
    setIsBanConfirmDialogOpen(true);
    setBanConfirmDialogTitle(`确定解封用户: ${userDetail?.name}`);
  };

  const handleUnbanConfirm = async (reason: string) => {
    const resp = await batchUnforbiddenUser({
      userIds: userId || '',
      forbiddenDesc: reason,
    });
    if (resp.status !== 200) {
      messageApi.error(`解封失败: ${resp.message}`);
      return;
    }
    messageApi.success('解封成功');
    setIsBanConfirmDialogOpen(false);
    void refetchUserDetail();
  };

  const handleBan = () => {
    setIsForbidden(true);
    setIsBanConfirmDialogOpen(true);
    setBanConfirmDialogTitle(`确定封禁用户: ${userDetail?.name}`);
  };

  const handleBanConfirm = async (reason: string) => {
    const resp = await batchForbiddenUser({
      userIds: userId || '',
      forbiddenDesc: reason,
    });
    if (resp.status !== 200) {
      messageApi.error(`封禁失败: ${resp.message}`);
      return;
    }
    messageApi.success('封禁成功');
    setIsBanConfirmDialogOpen(false);
    void refetchUserDetail();
  };

  const renderUserGender = useMemo(() => {
    switch (userDetail?.gender) {
      case Gender.MALE:
        return '男';
      case Gender.FEMALE:
        return '女';
      default:
        return '保密';
    }
  }, [userDetail?.gender]);

  return (
    <div className="user-mng-detail-container">
      {contextHolder}
      <div>
        <Button onClick={() => navigate('/user-mng')} icon={<LeftOutlined />} />
      </div>
      <div className="detail-content">
        <Flex justify="space-between">
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {userDetail?.name}
          </div>
          <Space size="small">
            <Button onClick={() => setIsHistoryDialogOpen(true)}>
              查看管理历史
            </Button>
            {alertAction()}
            {userStatusAction()}
          </Space>
        </Flex>
        <Flex
          align={isMobile ? 'flex-start' : 'center'}
          justify="space-between"
          gap={10}
          style={{ marginTop: 10 }}
          vertical={isMobile}
        >
          <Avatar src={userDetail?.headImageUrl} size={64} />

          <Flex vertical>
            <Flex>
              <div style={labelStyle}>SUPP ID:</div>
              <div>{userDetail?.userNumber}</div>
            </Flex>
            <Flex>
              <div style={labelStyle}>状态:</div>
              <Tag
                color={
                  userDetail?.userStatus === UserStatus.ACTIVE ? 'green' : 'red'
                }
              >
                {userDetail?.userStatusDesc}
              </Tag>
            </Flex>
            <Flex>
              <div style={labelStyle}>总作品数:</div>
              <div>{userDetail?.artworkTotalNum}</div>
            </Flex>
            <Flex>
              <div style={labelStyle}>Followers:</div>
              <div>{userDetail?.followNum}</div>
            </Flex>
            <Flex>
              <div style={labelStyle}>邮箱:</div>
              <div>{userDetail?.email}</div>
            </Flex>
          </Flex>

          <Flex vertical>
            <Flex>
              <div style={labelStyle}>作品审核不通过次数:</div>
              <div>{userDetail?.artworkReviewRejectNum}</div>
            </Flex>
            <Flex>
              <div style={labelStyle}>注册时间:</div>
              <div>{dayjs(userDetail?.createTime).format(FORMAT_TIME)}</div>
            </Flex>
            <Flex>
              <div style={labelStyle}>被点赞及收藏次数:</div>
              <div>{userDetail?.likeAndCollectionNum}</div>
            </Flex>
            <Flex>
              <div style={labelStyle}>年龄&性别:</div>
              <div>
                {userAge}&{renderUserGender}
              </div>
            </Flex>
          </Flex>
        </Flex>

        <Divider />

        <Table
          rowKey="artworkId"
          size="small"
          columns={columns}
          dataSource={artworkList?.records || []}
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            total: artworkList?.total,
            showSizeChanger: false,
            onChange: (page) => {
              setPage(page);
            },
          }}
        />
      </div>

      <BanConfirmDialog
        isForbidden={isForbidden}
        isOpen={isBanConfirmDialogOpen}
        onClose={() => setIsBanConfirmDialogOpen(false)}
        title={banConfirmDialogTitle}
        onConfirm={isForbidden ? handleBanConfirm : handleUnbanConfirm}
      />

      <HistoryDialog
        userId={userId || ''}
        isOpen={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
      />

      <AlertConfirmDialog
        type="single"
        user={userDetail}
        isOpen={isAlertConfirmDialogOpen}
        onClose={() => setIsAlertConfirmDialogOpen(false)}
        onConfirm={handleAlertConfirm}
      />

      <DeAlertConfirmDialog
        type="single"
        user={userDetail}
        isOpen={isDeAlertConfirmDialogOpen}
        onClose={() => setIsDeAlertConfirmDialogOpen(false)}
        onConfirm={handleDeAlertConfirm}
      />
    </div>
  );
};

export default DetailPage;
